import { add, addMilliseconds, endOfDay, isEqual, startOfDay } from 'date-fns';
import { DateTruncEnum } from '../../enums';

/**
 * @example
 * ```ts
 * const startDate = '2025-01-01T00:00:00.000Z';
 * const endDate = '2025-01-10T00:00:00.000Z';
 * const granularity = DateTruncEnum.Day;
 *
 * console.log(calculateDatesInRange(startDate, endDate, granularity));
 * // Output: [ '2025-01-01T00:00:00.000Z', '2025-01-02T00:00:00.000Z', '2025-01-03T00:00:00.000Z', ... ]
 * ```
 */
export const calculateDatesInRange = (
	startDate: string | Date,
	endDate: string | Date,
	granularity = DateTruncEnum.Day
) => {
	const start = startOfDay(new Date(startDate));
	const end = endOfDay(new Date(endDate));

	const filledDates: string[] = [];

	const incrementDate = (date: Date, granularity: DateTruncEnum, step = 1) => {
		switch (granularity) {
			case DateTruncEnum.Milliseconds:
				addMilliseconds(date, step);
				break;
			case DateTruncEnum.Second:
				add(date, { seconds: step });
				break;
			case DateTruncEnum.Minute:
				add(date, { minutes: step });
				break;
			case DateTruncEnum.Hour:
				add(date, { hours: step });
				break;
			case DateTruncEnum.Day:
				add(date, { days: step });
				break;
			case DateTruncEnum.Week:
				add(date, { weeks: step });
				break;
			case DateTruncEnum.Month:
				add(date, { months: step });
				break;
			case DateTruncEnum.Quarter:
				add(date, { months: step });
				break;
			case DateTruncEnum.Year:
				add(date, { years: step });
				break;
			default:
				break;
		}
	};

	// check if new date is inside the range
	// increment the date by the granularity
	for (let currentDate = start; currentDate <= end; incrementDate(currentDate, granularity)) {
		// push the date to the array
		filledDates.push(currentDate.toISOString());
	}

	return filledDates;
};

/**
 * @example
 * ```ts
 * const datesWithGaps: { date: string; value: number }[] = [
 *     { date: '2025-01-02T00:00:00.000Z', value: 4799 },
 *     { date: '2025-01-10T00:00:00.000Z', value: 40959 }
 * ];
 * const periodFrom = '2025-01-01T00:00:00.000Z';
 * const periodTo = '2025-01-30T00:00:00.000Z';
 * const granularity = DateTruncEnum.Day;
 *
 * console.log(fillDateGapsWithZeroValue(datesWithGaps, periodFrom, periodTo, granularity));
 * // Output: [ { date: '2025-01-01T00:00:00.000Z', value: 0 }, { date: '2025-01-02T00:00:00.000Z', value: 4799 }, ... ]
 * ```
 */
export const fillDateGapsWithZeroValue = (
	datesWithValues: { date: string | Date; value: number }[],
	startDate: string | Date,
	endDate: string | Date,
	granularity = DateTruncEnum.Day
) => {
	// generate all dates in the range
	const filledDates = calculateDatesInRange(startDate, endDate, granularity);

	// check if the date has a value
	return filledDates.map(date => {
		const found = datesWithValues.find(d => isEqual(new Date(d.date), new Date(date)));

		// if found, return the found value
		if (found) {
			return { date, value: found.value };
		}

		// if not found, return the date with 0 value
		return { date, value: 0 };
	});
};
